import type { AppConfig } from "../../config";

/**
 * A full-width band naming the environment and what is true in it.
 *
 * The sidebar badge says WHICH environment. This says what that MEANS, because
 * the two consoles are otherwise pixel-identical and the difference only
 * matters at the moment someone is about to write something.
 *
 * EVERY CLAUSE HERE MUST BE TRUE. The design's banner also read "posts to
 * #ai-control-changes"; we have no Slack integration, so that clause is
 * absent rather than aspirational. A banner is exactly where a false promise
 * does the most damage — it is the line an operator reads to decide how
 * careful to be, and one wrong clause makes the rest worth nothing.
 *
 * "read-back on every write" is claimed because we do it: every write control
 * calls `reload()` on its resource afterwards and renders server state, never
 * the value it optimistically sent.
 */
const BANNERS: Record<string, { label: string; detail: string; className: string }> = {
  prod: {
    label: "Production",
    detail: "eu-west-1 · writes affect live merchants · read-back on every write",
    className: "bg-brand-800 text-brand-on",
  },
  dev: {
    label: "Development",
    detail: "eu-west-1 · test merchants · safe to experiment",
    // The design's own band — dark slate, white text, the same weight as
    // production's. A pale grey strip reads as a caption; this reads as a
    // statement about where you are, which is the point of it.
    className: "bg-slate-700 text-white",
  },
};

export function EnvBanner({ config }: { config: AppConfig }) {
  const banner = BANNERS[config.env];

  // An unknown env is not "probably dev". config.json is fetched at runtime,
  // so an unrecognised value means the SPA and its deployment disagree — and
  // the honest response is to say so loudly rather than pick a default and be
  // confidently wrong about whether these are real merchants.
  if (!banner) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-amber-50 dark:bg-amber-400/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-amber-700 dark:text-amber-400">
        <span>Unknown environment</span>
        <span className="font-normal normal-case tracking-normal opacity-90">
          config.json reports “{config.env}”, which this console does not recognise. Treat
          every merchant here as real until someone confirms otherwise.
        </span>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] ${banner.className}`}
    >
      <span>{banner.label}</span>
      <span className="font-normal normal-case tracking-normal opacity-80">{banner.detail}</span>
    </div>
  );
}
